import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, ShoppingCart, Lock, LogOut } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useCart, useFanClub, useSettings } from "../../contexts/AppContext";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount, setIsCartOpen } = useCart();
  const { member, logout } = useFanClub();
  const { shop_enabled, fanclub_enabled, concerts_enabled } = useSettings();

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "Főoldal" },
    { to: "/zene", label: "Zene" },
    ...(concerts_enabled ? [{ to: "/koncertek", label: "Koncertek" }] : []),
    { to: "/hirek", label: "Hírek" },
    { to: "/rolam", label: "Rólam" },
    ...(fanclub_enabled && !member ? [{ to: "/fan-club", label: "Fan Club" }] : []),
    { to: "/kapcsolat", label: "Kapcsolat" },
  ];

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'glass py-3 sm:py-4' : 'bg-transparent py-4 sm:py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="nav-logo">
          <img src="/assets/images/logo-tz.png" alt="Tajti Zoltán" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          <span className="hidden sm:block text-lg sm:text-xl font-bold font-['Syne'] tracking-tight">Tajti Zoltán</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className={`text-sm font-medium transition-colors hover:text-primary ${location.pathname === link.to ? 'text-primary' : 'text-muted-foreground'}`}>
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {shop_enabled && (
            <Button variant="ghost" size="icon" className="relative h-9 w-9 sm:h-10 sm:w-10" onClick={() => setIsCartOpen(true)} data-testid="cart-button">
              <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5" />
              {cartCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-primary text-[10px] sm:text-xs rounded-full flex items-center justify-center">{cartCount}</span>}
            </Button>
          )}
          {fanclub_enabled && member && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" data-testid="user-menu-button">
                  <User className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/profilom" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    Profilom
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/vasarlasaim" className="flex items-center gap-2 cursor-pointer">
                    <ShoppingCart className="h-4 w-4" />
                    Vásárlásaim
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/jelszo-modositas" className="flex items-center gap-2 cursor-pointer">
                    <Lock className="h-4 w-4" />
                    Jelszó módosítás
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Kilépés
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 sm:h-10 sm:w-10" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-4 w-4 sm:h-5 sm:w-5" /> : <Menu className="h-4 w-4 sm:h-5 sm:w-5" />}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden glass mt-2 mx-4 rounded-xl p-4 sm:p-6 animate-fade-in">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 sm:py-3 text-base sm:text-lg font-medium transition-colors hover:text-primary ${location.pathname === link.to ? 'text-primary' : 'text-muted-foreground'}`}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
